import { FieldErrors } from "react-hook-form";
import { z } from "zod";
import { QuickbooksSyncBatchBillingMode, SyncQuickbooksBatchInput } from "generated/graphql";

const quickbooksLineSchema = z.object({
  amount: z.number().min(0, "QuickBooks line amounts cannot be negative."),
  billedAmount: z.number().min(0, "QuickBooks sync amounts cannot be negative.").nullish(),
  itemName: z.string().trim().min(1, "Every QuickBooks line needs an item name."),
});

export const quickbooksSyncBatchSchema = z
  .object({
    billingMode: z.nativeEnum(QuickbooksSyncBatchBillingMode),
    customerBillingAddressZip: z
      .string()
      .regex(/^\d*$/, "Zip fields should contain only numbers.")
      .nullish(),
    customerEmail: z
      .string()
      .email("Please enter a valid email address.")
      .or(z.literal(""))
      .nullish(),
    createEstimate: z.boolean().nullish(),
    createInvoice: z.boolean().nullish(),
    jobIds: z.array(z.string()).min(1, "Select at least one job for the QuickBooks batch."),
    lines: z.array(quickbooksLineSchema).min(1, "Add at least one QuickBooks line before syncing."),
    manualLines: z.array(quickbooksLineSchema).nullish(),
    partialAmountCents: z.number().nullish(),
    partialPercentage: z.number().nullish(),
    shipAddressAddressZip: z
      .string()
      .regex(/^\d*$/, "Zip fields should contain only numbers.")
      .nullish(),
  })
  .superRefine((input, context) => {
    if (!input.createInvoice && !input.createEstimate) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Select at least one item to create in QuickBooks (Invoice and/or Estimate).",
        path: ["createInvoice"],
      });
    }

    input.lines.forEach((line, index) => {
      if (line.billedAmount != null && line.billedAmount > line.amount) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "A QuickBooks sync amount cannot exceed its item amount.",
          path: ["lines", index, "billedAmount"],
        });
      }
    });

    const selectedTotal = [...input.lines, ...(input.manualLines ?? [])].reduce(
      (total, line) => total + line.amount,
      0
    );
    if (selectedTotal <= 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "The selected QuickBooks total must be greater than zero.",
        path: ["lines"],
      });
    }

    if (input.billingMode === QuickbooksSyncBatchBillingMode.PERCENTAGE) {
      if (
        !Number.isFinite(input.partialPercentage) ||
        !input.partialPercentage ||
        input.partialPercentage > 100
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Enter a percentage between 0 and 100.",
          path: ["partialPercentage"],
        });
      }
    }

    if (input.billingMode === QuickbooksSyncBatchBillingMode.FIXED_AMOUNT) {
      if (!Number.isFinite(input.partialAmountCents) || !input.partialAmountCents) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Enter a fixed amount greater than zero.",
          path: ["partialAmountCents"],
        });
      } else if (input.partialAmountCents > selectedTotal) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Fixed amount cannot exceed the selected total.",
          path: ["partialAmountCents"],
        });
      }
    }
  });

export const getQuickbooksSyncValidationError = (
  errors: FieldErrors<SyncQuickbooksBatchInput>
): string | undefined => {
  for (const error of Object.values(errors)) {
    if (typeof error?.message === "string") {
      return error.message;
    }

    if (error && typeof error === "object") {
      const nestedError = getQuickbooksSyncValidationError(
        error as FieldErrors<SyncQuickbooksBatchInput>
      );
      if (nestedError) {
        return nestedError;
      }
    }
  }
};
